<template>
  <div v-show="hasPassUsageReward" id="rewards" class="group -flex -wide -stack-space-single">
    <!-- success state -->
    <callout v-if="redeem.success" type="success">
      <div>{{ redeem.success }}</div>
    </callout>

    <!-- error state -->
    <callout v-if="errorMessage" type="error">
      <div>{{ errorMessage }}</div>
    </callout>

    <!-- loading state -->
    <skeleton-reward v-if="!rewardsReady && hasPassUsageReward" />

    <template v-for="reward in rewardsData">
      <pass-usage-reward-card
        v-if="
          hasPassUsageReward &&
          reward.type === 'pass_usage' &&
          passesData &&
          reward.typeMetaData.isLive
        "
        :key="reward.type"
        :reward="reward"
        :passes="passesData"
        icon=""
        :aria-label="''"
        @redeem="openModal"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * Reward Card
 */
import { computed, onBeforeMount, ref, shallowRef, watch } from 'vue'
import { fbApi } from '@/utils/http-api'
import type { Reward, Rewards } from '@/types/reward-types'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import Callout from '@/components/notifications/callout.vue'
import SkeletonReward from '@/components/loaders/skeleton-reward.vue'
import PassUsageRewardCard from '@/components/users/rewards/pass-usage-reward-card.vue'
import type { PassUsageStats } from '@/types/calendar-types'

/** PROPS **/
// noop

/** STATE **/
const userAuthStore = useUserAuthStore()
const modalsStore = useModalsStore()

const widget = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})
const redeem = ref<{
  loading: boolean
  success: string
  error: string
}>({
  loading: false,
  success: '',
  error: '',
})

const rewardsData = shallowRef<Rewards | null>(null)
const passesData = ref<PassUsageStats | null>(null)

/** NON REACTIVE **/
const modalId = 'reward-widget-modal'

/** COMPUTED **/
const rewardsReady = computed(() => {
  return rewardsData.value && rewardsData.value && !widget.value.error && !widget.value.loading
})

const errorMessage = computed(() => {
  return widget.value.error || redeem.value.error
})

// TODO: pass reward - must update this computed if we have other rewards
// powers top div to hide spacing and pass usage reward
const hasPassUsageReward = computed(() => {
  return !!(userAuthStore.isFreeUser || userAuthStore.isPlusPassSubscriber)
})

/** LIFECYCLE **/
onBeforeMount(async () => {
  await fetchRewards()
})

/** WATCHERS **/
watch(
  () => modalsStore.simpleModal.action,
  (newAction, prevAction) => {
    // unmark day complete if user selects "no" (close)
    if (
      modalsStore.simpleModal.id === modalId &&
      newAction !== prevAction &&
      newAction === 'confirm'
    ) {
      const rewardId = modalsStore.simpleModal.props.rewardId
      redeemReward(rewardId)
    }
  },
)

/** METHODS **/
// fetch rewards data
async function fetchRewards() {
  widget.value.loading = true

  try {
    const res = await fbApi.get('/my/rewards')

    if (!res || !res.body) {
      throw new Error(`Unable to fetch rewards, please refresh the page and try again`)
    }

    const {
      rewards,
      passes,
    }: {
      rewards: Rewards
      passes: PassUsageStats
    } = res.body.data

    widget.value.error = ''
    widget.value.loading = false

    rewardsData.value = rewards
    passesData.value = passes
  } catch (err) {
    widget.value.error = `Unable to fetch Rewards, please refresh the page and try again`
    widget.value.loading = false
  }
}

async function redeemReward(rewardId: number) {
  if (redeem.value.loading) {
    return
  }

  redeem.value.loading = true

  const url = `/my/rewards/${rewardId}/redeem`

  try {
    const res = await fbApi.post(url)

    if (!res || !res.body) {
      throw new Error(`Unable to redeem reward, please try again`)
    }

    redeem.value.success = 'Reward Redeemed!'
    redeem.value.error = ''
    redeem.value.loading = false

    const {
      reward,
    }: {
      reward: Reward
    } = res.body.data

    replaceReward(reward)

    // not all rewards will have passes
    if (res.body.data.passes) {
      const {
        passes,
      }: {
        passes: PassUsageStats
      } = res.body.data

      replacePasses(passes)
      redeem.value.success = 'Reward Redeemed! Activate your pass below.'
    }
  } catch (err) {
    redeem.value.success = ''
    redeem.value.error = `Unable to Redeem Reward, please try again`
    redeem.value.loading = false
  }
}

function replaceReward(reward: Reward) {
  rewardsData.value = rewardsData.value!.map((r) => {
    if (r.id === reward.id) {
      return reward
    }

    return r
  })
}

function replacePasses(passStats: PassUsageStats) {
  passesData.value = passStats
}

function openModal({
  rewardId,
  heading,
  subheading,
}: {
  rewardId: string
  heading: string
  subheading: string
}) {
  modalsStore.openSimpleModal({
    type: 'CONFIRM',
    id: modalId,
    props: {
      heading: heading,
      subheading: subheading,
      buttonText: 'Redeem',
      rewardId,
    },
  })
}
</script>
